import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { formatOptionalDate } from '../utils/format';

const REASON_MAX_LENGTH = 130; // AdjustInvoiceDueDate::MAX_REASON_LENGTH (Servman note 255)

interface Props {
  applyTo: string;
  company: string;
  currentDueDate: string | null;
  isSubmitting: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (dueDate: string, reason: string) => void;
}

const AdjustDueDateModal: React.FC<Props> = ({
  applyTo,
  company,
  currentDueDate,
  isSubmitting,
  error,
  onClose,
  onConfirm,
}) => {
  const [dueDate, setDueDate] = useState('');
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();

  const handleClose = () => {
    setDueDate('');
    setReason('');
    onClose();
  };

  return (
    <Dialog open onClose={isSubmitting ? undefined : handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit due date — Invoice #{applyTo}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {company}. Current due date: {formatOptionalDate(currentDueDate)}. The change and reason are recorded in the
          client notes.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label="New due date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
          autoFocus
          sx={{ mb: 2 }}
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          required
          inputProps={{ maxLength: REASON_MAX_LENGTH }}
          helperText={`${reason.length}/${REASON_MAX_LENGTH}`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!dueDate || !trimmed || isSubmitting}
          onClick={() => onConfirm(dueDate, trimmed)}
        >
          {isSubmitting ? 'Saving…' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustDueDateModal;
